import type {
  EventNotification,
  NotificationEventType,
} from '@indutrust/notification-model';

/** Event types with a matching `descriptions.<event_type>` Transloco key. */
export const NOTIFICATION_TYPES_WITH_I18N_DESCRIPTION = new Set<
  NotificationEventType | 'default'
>([
  'capability_suggestion_approved',
  'capability_suggestion_rejected',
  'capability_suggestion_submitted',
  'default',
  'factory_published',
  'factory_rejected',
  'machine_submitted_for_review',
  'profile_viewed',
  'project_factory_added',
  'submit_for_approval_arrival',
  'supplier_registered',
]);

/** Params passed to `descriptions.<event_type>` Transloco keys. */
export type NotificationDescriptionParams = Record<string, string | number>;

function readScalarPayloadValue(
  payload: Record<string, unknown>,
  key: string
): string | undefined {
  const value = payload[key];

  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return undefined;
}

function readFactoryName(
  notification: EventNotification,
  payload: Record<string, unknown>
): string | undefined {
  const fromPayload =
    readScalarPayloadValue(payload, 'factory_name') ??
    readScalarPayloadValue(payload, 'factory');

  if (fromPayload) {
    return fromPayload;
  }

  if (notification.target_type === 'Factory') {
    return readScalarPayloadValue(payload, 'name');
  }

  return undefined;
}

function readProjectName(
  notification: EventNotification,
  payload: Record<string, unknown>
): string | undefined {
  const fromPayload =
    readScalarPayloadValue(payload, 'project_name') ??
    readScalarPayloadValue(payload, 'project');

  if (fromPayload) {
    return fromPayload;
  }

  if (notification.target_type === 'Project') {
    return readScalarPayloadValue(payload, 'name');
  }

  return undefined;
}

function applyMachineSubmittedLabel(
  params: NotificationDescriptionParams,
  machineName: string | undefined,
  brand: string | undefined
): void {
  // Fallback label when payload omits name/brand (generic admin banner still reads).
  if (machineName === undefined) {
    params['machineLabel'] = brand ?? 'A machine';
    return;
  }

  if (brand === undefined) {
    params['machineLabel'] = machineName;
    return;
  }

  params['machineLabel'] = `${machineName} (${brand})`;
}

function applyCapabilitySuggestionParams(
  params: NotificationDescriptionParams,
  notification: EventNotification,
  payload: Record<string, unknown>
): void {
  const suggestionName = readScalarPayloadValue(payload, 'suggestion_name');
  if (suggestionName !== undefined) {
    params['suggestionName'] = suggestionName;
  }

  const kind = readScalarPayloadValue(payload, 'kind');
  if (kind !== undefined) {
    params['kind'] = kind;
  }

  const rejectionReason = readScalarPayloadValue(payload, 'rejection_reason');
  if (rejectionReason !== undefined) {
    params['rejectionReason'] = rejectionReason;
  }

  const eventType = notification.event_type;
  if (
    eventType === 'capability_suggestion_submitted' ||
    eventType === 'capability_suggestion_approved' ||
    eventType === 'capability_suggestion_rejected'
  ) {
    params['suggestionName'] = suggestionName ?? 'A capability suggestion';
  }

  if (eventType === 'capability_suggestion_rejected') {
    params['rejectionReason'] = rejectionReason ?? '—';
  }
}

/**
 * Reads scalar payload fields for i18n interpolation in notification subtitles.
 *
 * Only exposes aggregate counts and resource names the recipient already owns.
 */
export function extractNotificationDescriptionParams(
  notification: EventNotification
): NotificationDescriptionParams {
  const { payload } = notification;
  if (!payload) {
    return {};
  }

  const params: NotificationDescriptionParams = {};

  const viewCount = readScalarPayloadValue(payload, 'view_count');
  if (viewCount !== undefined) {
    params['count'] = viewCount;
  }

  const factoryName = readFactoryName(notification, payload);
  if (factoryName !== undefined) {
    params['factoryName'] = factoryName;
  }

  const projectName = readProjectName(notification, payload);
  if (projectName !== undefined) {
    params['projectName'] = projectName;
  }

  const machineName = readScalarPayloadValue(payload, 'machine_name');
  if (machineName !== undefined) {
    params['machineName'] = machineName;
  }

  const brand = readScalarPayloadValue(payload, 'brand');
  if (brand !== undefined) {
    params['brand'] = brand;
  }

  applyCapabilitySuggestionParams(params, notification, payload);

  if (notification.event_type === 'machine_submitted_for_review') {
    applyMachineSubmittedLabel(params, machineName, brand);
  }

  return params;
}

/** Whether a Transloco `descriptions.*` fallback exists for the event type. */
export function hasI18nNotificationDescription(
  eventType: NotificationEventType | undefined
): boolean {
  return NOTIFICATION_TYPES_WITH_I18N_DESCRIPTION.has(eventType ?? 'default');
}

/** Trimmed API subtitle when the server provides human-readable text. */
export function resolveNotificationMessage(
  notification: EventNotification
): string | undefined {
  const trimmed = notification.message?.trim();
  return trimmed?.length ? trimmed : undefined;
}
